// src/tripStatus.ts
var TripStatus = /* @__PURE__ */ ((TripStatus2) => {
  TripStatus2["CREATED"] = "created";
  TripStatus2["SEARCHING"] = "searching";
  TripStatus2["ACCEPTED"] = "accepted";
  TripStatus2["DRIVER_ARRIVED"] = "driverArrived";
  TripStatus2["IN_PROGRESS"] = "inProgress";
  TripStatus2["INTERMEDIATE_STOP_ARRIVED"] = "intermediateStopArrived";
  TripStatus2["INTERMEDIATE_STOP_SKIPPED"] = "intermediateStopSkipped";
  TripStatus2["DESTINATION_ARRIVED"] = "destinationArrived";
  TripStatus2["COMPLETED"] = "completed";
  TripStatus2["NOT_FOUND"] = "notFound";
  TripStatus2["CANCELLED"] = "cancelled";
  TripStatus2["EXPIRED"] = "expired";
  return TripStatus2;
})(TripStatus || {});
var TRIP_STATUS_VALUES = Object.values(TripStatus);

// src/redisKeys.ts
function getAvailableDriversKey(vehicleCategoryID) {
  return `available_drivers:location:${vehicleCategoryID}`;
}
function getBusyDriversKey(vehicleCategoryID) {
  return `busy_drivers:location:${vehicleCategoryID}`;
}
var DRIVER_LAST_SEEN_KEY = "drivers:last_seen";
function getDriverAvailabilityKey(driverID) {
  return `driver:availability:${driverID}`;
}

// src/driver.ts
var DriverAvailability = /* @__PURE__ */ ((DriverAvailability2) => {
  DriverAvailability2["ONLINE"] = "online";
  DriverAvailability2["OFFLINE"] = "offline";
  DriverAvailability2["UNDER_TRIP"] = "underTrip";
  return DriverAvailability2;
})(DriverAvailability || {});

// src/adminAccess.ts
var AdminRole = /* @__PURE__ */ ((AdminRole2) => {
  AdminRole2["CEO"] = "ceo";
  AdminRole2["BRANCH_MANAGER"] = "branchManager";
  AdminRole2["REGISTRATION_TEAM"] = "registrationTeam";
  AdminRole2["OPERATIONS_TEAM"] = "operationsTeam";
  AdminRole2["SUPPORT_TEAM"] = "supportTeam";
  AdminRole2["FINANCE_TEAM"] = "financeTeam";
  return AdminRole2;
})(AdminRole || {});
var ADMIN_PERMISSIONS = [
  "dashboard.view",
  "live.view",
  "trips.view",
  /** Cancel / reassign / force-complete a trip on someone's behalf. */
  "trips.intervene",
  "drivers.view",
  /** Onboarding, document review, approval. */
  "drivers.onboard",
  "drivers.edit",
  /** Suspend / reactivate / block. */
  "drivers.status",
  "drivers.delete",
  "riders.view",
  "riders.status",
  "pricing.view",
  "pricing.manage",
  "promos.view",
  "promos.manage",
  "finance.view",
  /** Moves or books money: manual transactions, settlements, debt limits. */
  "finance.manage",
  "payouts.view",
  "payouts.manage",
  /** LATRA / CeTS and VFD tax receipts. */
  "compliance.view",
  "compliance.manage",
  "reports.view",
  /** Operations, driver and rider analytics (no money beyond per-driver earnings). */
  "analytics.view",
  "sos.view",
  "sos.manage",
  "support.view",
  "support.manage",
  "content.manage",
  "settings.manage",
  "admins.manage",
  "audit.view",
  "system.debug",
  /** Referral program (spec §9): agent application review and creation. */
  "agents.review",
  "referrals.view",
  /** Disable referrals, suspend / reactivate agents, change a referrer. */
  "referrals.manage",
  /** Program amounts, windows, on/off, minimum cash-out; reversing a paid reward. */
  "referrals.configure",
  /** The tag catalogue. Putting a tag on an account needs drivers.edit / riders.status. */
  "tags.manage"
];
var OPS_CORE = [
  "dashboard.view",
  "live.view",
  "trips.view",
  "trips.intervene",
  "sos.view",
  "sos.manage"
];
var DRIVER_ADMIN = [
  "drivers.view",
  "drivers.onboard",
  "drivers.edit",
  "drivers.status",
  "drivers.delete"
];
var ROLE_PERMISSIONS = {
  ["ceo" /* CEO */]: ADMIN_PERMISSIONS,
  // COO: runs the whole operation and sees all money, but money-moving,
  // admin accounts, platform settings and debug stay with CEO / finance.
  ["branchManager" /* BRANCH_MANAGER */]: [
    ...OPS_CORE,
    ...DRIVER_ADMIN,
    "riders.view",
    "riders.status",
    "pricing.view",
    "pricing.manage",
    "promos.view",
    "promos.manage",
    "finance.view",
    "payouts.view",
    "compliance.view",
    "reports.view",
    "analytics.view",
    "support.view",
    "support.manage",
    "audit.view",
    "agents.review",
    "referrals.view",
    "referrals.manage",
    "tags.manage"
  ],
  ["operationsTeam" /* OPERATIONS_TEAM */]: [
    ...OPS_CORE,
    ...DRIVER_ADMIN,
    "riders.view",
    "riders.status",
    "pricing.view",
    "pricing.manage",
    "analytics.view",
    "agents.review",
    "referrals.view",
    "tags.manage"
  ],
  // trips.view: the driver page lists that driver's trips.
  ["registrationTeam" /* REGISTRATION_TEAM */]: [
    "dashboard.view",
    "trips.view",
    ...DRIVER_ADMIN,
    "agents.review",
    "referrals.view"
  ],
  ["supportTeam" /* SUPPORT_TEAM */]: [
    "dashboard.view",
    "trips.view",
    "drivers.view",
    "riders.view",
    "riders.status",
    "sos.view",
    "sos.manage",
    "support.view",
    "support.manage"
  ],
  ["financeTeam" /* FINANCE_TEAM */]: [
    "dashboard.view",
    "trips.view",
    "drivers.view",
    "riders.view",
    "promos.view",
    "finance.view",
    "finance.manage",
    "payouts.view",
    "payouts.manage",
    "compliance.view",
    "compliance.manage",
    "reports.view",
    "analytics.view",
    "referrals.view"
  ]
};
var LEGACY_ROLE_ALIASES = {
  superadmin: "ceo" /* CEO */
};
function isAdminRole(value) {
  return typeof value === "string" && Object.values(AdminRole).includes(value);
}
function normalizeAdminRole(role) {
  if (!role) return null;
  if (isAdminRole(role)) return role;
  return LEGACY_ROLE_ALIASES[role] ?? null;
}
function permissionsForRole(role) {
  const normalized = normalizeAdminRole(role);
  return normalized ? ROLE_PERMISSIONS[normalized] : [];
}
function roleHasPermission(role, permission) {
  return permissionsForRole(role).includes(permission);
}

// src/cashback.ts
var CashbackStatus = /* @__PURE__ */ ((CashbackStatus2) => {
  CashbackStatus2["GRANTED"] = "granted";
  CashbackStatus2["REVERSED"] = "reversed";
  return CashbackStatus2;
})(CashbackStatus || {});
var CASHBACK_RATE_MIN = 0;
var CASHBACK_RATE_MAX = 100;
var CASHBACK_DEFAULT_PASSENGER_PERCENTAGE = 5;
var CASHBACK_DEFAULT_DRIVER_PERCENTAGE = 5;

// src/partPayment.ts
var PART_WALLET_DEFAULT_CASH_STEP = 500;
var PART_WALLET_DEFAULT_REVIEW_HOURS = 24;
var CASH_SETTLEMENT_ADMIN_CLOSED_UNPAID = "adminClosedUnpaid";

// src/referral.ts
var ReferralProgram = /* @__PURE__ */ ((ReferralProgram2) => {
  ReferralProgram2["AGENT_TO_DRIVER"] = "agentToDriver";
  ReferralProgram2["DRIVER_TO_DRIVER"] = "driverToDriver";
  return ReferralProgram2;
})(ReferralProgram || {});
var ReferrerType = /* @__PURE__ */ ((ReferrerType2) => {
  ReferrerType2["AGENT"] = "agent";
  ReferrerType2["DRIVER"] = "driver";
  return ReferrerType2;
})(ReferrerType || {});
var ReferralStatus = /* @__PURE__ */ ((ReferralStatus2) => {
  ReferralStatus2["PENDING"] = "pending";
  ReferralStatus2["IN_PROGRESS"] = "inProgress";
  ReferralStatus2["COMPLETED"] = "completed";
  ReferralStatus2["CLOSED"] = "closed";
  return ReferralStatus2;
})(ReferralStatus || {});
var ReferralTierState = /* @__PURE__ */ ((ReferralTierState2) => {
  ReferralTierState2["LOCKED"] = "locked";
  ReferralTierState2["PAID"] = "paid";
  ReferralTierState2["EXPIRED"] = "expired";
  ReferralTierState2["CANCELLED"] = "cancelled";
  ReferralTierState2["REVERSED"] = "reversed";
  return ReferralTierState2;
})(ReferralTierState || {});
var ReferralSide = /* @__PURE__ */ ((ReferralSide2) => {
  ReferralSide2["INVITER"] = "inviter";
  ReferralSide2["INVITEE"] = "invitee";
  return ReferralSide2;
})(ReferralSide || {});
var ReferralMilestone = /* @__PURE__ */ ((ReferralMilestone2) => {
  ReferralMilestone2["ACTIVATION"] = "activation";
  ReferralMilestone2["TRIPS"] = "trips";
  return ReferralMilestone2;
})(ReferralMilestone || {});
var ReferralWindowAnchor = /* @__PURE__ */ ((ReferralWindowAnchor2) => {
  ReferralWindowAnchor2["SIGNUP"] = "signup";
  ReferralWindowAnchor2["ACTIVATION"] = "activation";
  ReferralWindowAnchor2["FIRST_TRIP"] = "firstTrip";
  return ReferralWindowAnchor2;
})(ReferralWindowAnchor || {});
var REFERRAL_PRODUCT_TYPES = ["car", "motorcycle", "tricycle"];
var REFERRAL_MAX_TIERS = 10;
var REFERRAL_MAX_AMOUNT = 1e6;
var REFERRAL_MAX_WINDOW_DAYS = 365;
var REFERRAL_DEFAULT_INVITER_TIERS = [
  {
    milestone: "activation" /* ACTIVATION */,
    tripCount: 0,
    amount: 2e3,
    windowDays: 15,
    windowFrom: "signup" /* SIGNUP */
  },
  {
    milestone: "trips" /* TRIPS */,
    tripCount: 1,
    amount: 3e3,
    windowDays: 15,
    windowFrom: "activation" /* ACTIVATION */
  },
  {
    milestone: "trips" /* TRIPS */,
    tripCount: 5,
    amount: 5e3,
    windowDays: 30,
    windowFrom: "firstTrip" /* FIRST_TRIP */
  }
];
var REFERRAL_DEFAULT_INVITEE_TIERS = [
  {
    milestone: "trips" /* TRIPS */,
    tripCount: 5,
    amount: 5e3,
    windowDays: 30,
    windowFrom: "firstTrip" /* FIRST_TRIP */
  }
];
var AGENT_DEFAULT_MIN_CASHOUT = 5e3;
var REFERRAL_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
var REFERRAL_CODE_BODY_LENGTH = 6;
var AGENT_CODE_PREFIX = "AG";
var DRIVER_CODE_PREFIX = "DR";
var CODE_RE = new RegExp(
  `^(${AGENT_CODE_PREFIX}|${DRIVER_CODE_PREFIX})[${REFERRAL_CODE_ALPHABET}]{${REFERRAL_CODE_BODY_LENGTH}}$`
);
function normalizeReferralCode(input) {
  return input.toUpperCase().replace(/[\s-]/g, "");
}
function isReferralCodeFormat(code) {
  return CODE_RE.test(code);
}
function referrerTypeOfCode(code) {
  if (!isReferralCodeFormat(code)) return null;
  return code.startsWith(AGENT_CODE_PREFIX) ? "agent" /* AGENT */ : "driver" /* DRIVER */;
}
function programForReferrer(type) {
  return type === "agent" /* AGENT */ ? "agentToDriver" /* AGENT_TO_DRIVER */ : "driverToDriver" /* DRIVER_TO_DRIVER */;
}
var AgentStatus = /* @__PURE__ */ ((AgentStatus2) => {
  AgentStatus2["DRAFT"] = "draft";
  AgentStatus2["UNDER_REVIEW"] = "underReview";
  AgentStatus2["REJECTED"] = "rejected";
  AgentStatus2["APPROVED"] = "approved";
  AgentStatus2["SUSPENDED"] = "suspended";
  AgentStatus2["DELETED"] = "deleted";
  return AgentStatus2;
})(AgentStatus || {});
var AgentRejectionReason = /* @__PURE__ */ ((AgentRejectionReason2) => {
  AgentRejectionReason2["ID_UNCLEAR"] = "idUnclear";
  AgentRejectionReason2["ID_NOT_VALID"] = "idNotValid";
  AgentRejectionReason2["NAME_MISMATCH"] = "nameMismatch";
  AgentRejectionReason2["PHOTO_MISMATCH"] = "photoMismatch";
  AgentRejectionReason2["DOB_MISMATCH"] = "dobMismatch";
  AgentRejectionReason2["UNDER_18"] = "under18";
  AgentRejectionReason2["DUPLICATE"] = "duplicate";
  AgentRejectionReason2["OTHER"] = "other";
  return AgentRejectionReason2;
})(AgentRejectionReason || {});
var AGENT_REJECTION_REASON_LABELS = {
  ["idUnclear" /* ID_UNCLEAR */]: { en: "ID image unclear", sw: "Picha ya kitambulisho haionekani vizuri" },
  ["idNotValid" /* ID_NOT_VALID */]: { en: "ID not a valid government ID", sw: "Kitambulisho si cha serikali halali" },
  ["nameMismatch" /* NAME_MISMATCH */]: { en: "Name does not match ID", sw: "Jina halilingani na kitambulisho" },
  ["photoMismatch" /* PHOTO_MISMATCH */]: { en: "Profile photo does not match ID", sw: "Picha haifanani na kitambulisho" },
  ["dobMismatch" /* DOB_MISMATCH */]: { en: "Date of birth does not match ID", sw: "Tarehe ya kuzaliwa hailingani na kitambulisho" },
  ["under18" /* UNDER_18 */]: { en: "Under 18", sw: "Chini ya miaka 18" },
  ["duplicate" /* DUPLICATE */]: { en: "Duplicate account", sw: "Akaunti imejirudia" },
  ["other" /* OTHER */]: { en: "Other", sw: "Nyingine" }
};
var AgentHeardFrom = /* @__PURE__ */ ((AgentHeardFrom2) => {
  AgentHeardFrom2["SAFIRI_STAFF"] = "safiriStaff";
  AgentHeardFrom2["ANOTHER_AGENT"] = "anotherAgent";
  AgentHeardFrom2["SOCIAL_MEDIA"] = "socialMedia";
  AgentHeardFrom2["OTHER"] = "other";
  return AgentHeardFrom2;
})(AgentHeardFrom || {});
var AGENT_GENDERS = ["male", "female"];
var AGENT_MIN_AGE = 18;
var AGENT_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
var AGENT_UPLOAD_MIME_TYPES = ["image/jpeg", "image/png", "application/pdf"];
var ReferredDriverStatus = /* @__PURE__ */ ((ReferredDriverStatus2) => {
  ReferredDriverStatus2["DOCUMENTS_PENDING"] = "documentsPending";
  ReferredDriverStatus2["UNDER_REVIEW"] = "underReview";
  ReferredDriverStatus2["ACTIVE"] = "active";
  ReferredDriverStatus2["REJECTED"] = "rejected";
  ReferredDriverStatus2["DOCUMENTS_EXPIRED"] = "documentsExpired";
  ReferredDriverStatus2["SUSPENDED"] = "suspended";
  ReferredDriverStatus2["DELETED"] = "deleted";
  return ReferredDriverStatus2;
})(ReferredDriverStatus || {});
var ReferralFlagType = /* @__PURE__ */ ((ReferralFlagType2) => {
  ReferralFlagType2["SHARED_PAYOUT_ACCOUNT"] = "sharedPayoutAccount";
  ReferralFlagType2["REPEAT_RIDER"] = "repeatRider";
  ReferralFlagType2["PAYOUT_NAME_MISMATCH"] = "payoutNameMismatch";
  return ReferralFlagType2;
})(ReferralFlagType || {});
var ReferralFlagStatus = /* @__PURE__ */ ((ReferralFlagStatus2) => {
  ReferralFlagStatus2["OPEN"] = "open";
  ReferralFlagStatus2["DISMISSED"] = "dismissed";
  ReferralFlagStatus2["ACTIONED"] = "actioned";
  return ReferralFlagStatus2;
})(ReferralFlagStatus || {});
var REFERRAL_REPEAT_RIDER_THRESHOLD = 3;
var AgentNotificationType = /* @__PURE__ */ ((AgentNotificationType2) => {
  AgentNotificationType2["APPLICATION_RECEIVED"] = "applicationReceived";
  AgentNotificationType2["APPLICATION_APPROVED"] = "applicationApproved";
  AgentNotificationType2["APPLICATION_REJECTED"] = "applicationRejected";
  AgentNotificationType2["REFERRAL_SIGNED_UP"] = "referralSignedUp";
  AgentNotificationType2["REFERRAL_ACTIVE"] = "referralActive";
  AgentNotificationType2["TIER_PAID"] = "tierPaid";
  AgentNotificationType2["DEADLINE_SOON"] = "deadlineSoon";
  AgentNotificationType2["TIER_EXPIRED"] = "tierExpired";
  AgentNotificationType2["REFERRAL_DISABLED"] = "referralDisabled";
  AgentNotificationType2["REWARD_REVERSED"] = "rewardReversed";
  AgentNotificationType2["REFERRED_DRIVER_DELETED"] = "referredDriverDeleted";
  AgentNotificationType2["CASHOUT_SUCCEEDED"] = "cashoutSucceeded";
  AgentNotificationType2["CASHOUT_FAILED"] = "cashoutFailed";
  AgentNotificationType2["ACCOUNT_SUSPENDED"] = "accountSuspended";
  AgentNotificationType2["ACCOUNT_REACTIVATED"] = "accountReactivated";
  return AgentNotificationType2;
})(AgentNotificationType || {});

// src/tanzaniaRegions.ts
var TANZANIA_REGIONS = {
  Arusha: ["Arusha City", "Arusha", "Karatu", "Longido", "Meru", "Monduli", "Ngorongoro"],
  "Dar es Salaam": ["Ilala", "Kinondoni", "Temeke", "Ubungo", "Kigamboni"],
  Dodoma: ["Dodoma City", "Bahi", "Chamwino", "Chemba", "Kondoa", "Kongwa", "Mpwapwa"],
  Geita: ["Geita", "Bukombe", "Chato", "Mbogwe", "Nyang'hwale"],
  Iringa: ["Iringa Municipal", "Iringa", "Kilolo", "Mafinga Town", "Mufindi"],
  Kagera: ["Bukoba Municipal", "Bukoba", "Biharamulo", "Karagwe", "Kyerwa", "Missenyi", "Muleba", "Ngara"],
  Katavi: ["Mpanda Municipal", "Mlele", "Mpimbwe", "Nsimbo", "Tanganyika"],
  Kigoma: ["Kigoma-Ujiji Municipal", "Kigoma", "Buhigwe", "Kakonko", "Kasulu", "Kibondo", "Uvinza"],
  Kilimanjaro: ["Moshi Municipal", "Moshi", "Hai", "Mwanga", "Rombo", "Same", "Siha"],
  Lindi: ["Lindi Municipal", "Mtama", "Kilwa", "Liwale", "Nachingwea", "Ruangwa"],
  Manyara: ["Babati Town", "Babati", "Hanang", "Kiteto", "Mbulu", "Simanjiro"],
  Mara: ["Musoma Municipal", "Musoma", "Bunda", "Butiama", "Rorya", "Serengeti", "Tarime"],
  Mbeya: ["Mbeya City", "Mbeya", "Busokelo", "Chunya", "Kyela", "Mbarali", "Rungwe"],
  Morogoro: ["Morogoro Municipal", "Morogoro", "Gairo", "Kilombero", "Kilosa", "Malinyi", "Mvomero", "Ulanga", "Ifakara Town"],
  Mtwara: ["Mtwara Municipal", "Mtwara", "Masasi", "Nanyumbu", "Newala", "Tandahimba"],
  Mwanza: ["Ilemela", "Nyamagana", "Buchosa", "Kwimba", "Magu", "Misungwi", "Sengerema", "Ukerewe"],
  Njombe: ["Njombe Town", "Njombe", "Ludewa", "Makambako Town", "Makete", "Wanging'ombe"],
  Pwani: ["Kibaha Town", "Kibaha", "Bagamoyo", "Chalinze", "Kibiti", "Kisarawe", "Mafia", "Mkuranga", "Rufiji"],
  Rukwa: ["Sumbawanga Municipal", "Sumbawanga", "Kalambo", "Nkasi"],
  Ruvuma: ["Songea Municipal", "Songea", "Madaba", "Mbinga", "Namtumbo", "Nyasa", "Tunduru"],
  Shinyanga: ["Shinyanga Municipal", "Shinyanga", "Kahama", "Kishapu", "Msalala", "Ushetu"],
  Simiyu: ["Bariadi", "Busega", "Itilima", "Maswa", "Meatu"],
  Singida: ["Singida Municipal", "Singida", "Ikungi", "Iramba", "Manyoni", "Mkalama", "Itigi"],
  Songwe: ["Ileje", "Mbozi", "Momba", "Songwe", "Tunduma Town"],
  Tabora: ["Tabora Municipal", "Igunga", "Kaliua", "Nzega", "Sikonge", "Urambo", "Uyui"],
  Tanga: ["Tanga City", "Handeni", "Kilindi", "Korogwe", "Lushoto", "Bumbuli", "Mkinga", "Muheza", "Pangani"],
  "Kaskazini Pemba": ["Micheweni", "Wete"],
  "Kusini Pemba": ["Chake Chake", "Mkoani"],
  "Kaskazini Unguja": ["Kaskazini A", "Kaskazini B"],
  "Kusini Unguja": ["Kati", "Kusini"],
  "Mjini Magharibi": ["Mjini", "Magharibi A", "Magharibi B"]
};
var TANZANIA_REGION_NAMES = Object.keys(TANZANIA_REGIONS).sort();
function isTanzaniaDistrict(region, district) {
  return TANZANIA_REGIONS[region]?.includes(district) ?? false;
}

// src/tags.ts
var TAG_SUBJECTS = ["driver", "rider"];
var TAG_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var TAG_KEY_MAX = 40;

export { ADMIN_PERMISSIONS, AGENT_CODE_PREFIX, AGENT_DEFAULT_MIN_CASHOUT, AGENT_GENDERS, AGENT_MIN_AGE, AGENT_REJECTION_REASON_LABELS, AGENT_UPLOAD_MAX_BYTES, AGENT_UPLOAD_MIME_TYPES, AdminRole, AgentHeardFrom, AgentNotificationType, AgentRejectionReason, AgentStatus, CASHBACK_DEFAULT_DRIVER_PERCENTAGE, CASHBACK_DEFAULT_PASSENGER_PERCENTAGE, CASHBACK_RATE_MAX, CASHBACK_RATE_MIN, CASH_SETTLEMENT_ADMIN_CLOSED_UNPAID, CashbackStatus, DRIVER_CODE_PREFIX, DRIVER_LAST_SEEN_KEY, DriverAvailability, PART_WALLET_DEFAULT_CASH_STEP, PART_WALLET_DEFAULT_REVIEW_HOURS, REFERRAL_CODE_ALPHABET, REFERRAL_CODE_BODY_LENGTH, REFERRAL_DEFAULT_INVITEE_TIERS, REFERRAL_DEFAULT_INVITER_TIERS, REFERRAL_MAX_AMOUNT, REFERRAL_MAX_TIERS, REFERRAL_MAX_WINDOW_DAYS, REFERRAL_PRODUCT_TYPES, REFERRAL_REPEAT_RIDER_THRESHOLD, ROLE_PERMISSIONS, ReferralFlagStatus, ReferralFlagType, ReferralMilestone, ReferralProgram, ReferralSide, ReferralStatus, ReferralTierState, ReferralWindowAnchor, ReferredDriverStatus, ReferrerType, TAG_KEY_MAX, TAG_KEY_PATTERN, TAG_SUBJECTS, TANZANIA_REGIONS, TANZANIA_REGION_NAMES, TRIP_STATUS_VALUES, TripStatus, getAvailableDriversKey, getBusyDriversKey, getDriverAvailabilityKey, isAdminRole, isReferralCodeFormat, isTanzaniaDistrict, normalizeAdminRole, normalizeReferralCode, permissionsForRole, programForReferrer, referrerTypeOfCode, roleHasPermission };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map